import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Grid, IconButton, Radio, Tooltip, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useModal } from 'src/hooks';
import { lang, useUserRole } from 'src/utils';

import { SimpleDialog } from '../modal';
import { CustomTooltip } from '../tooltip/CustomTooltip';
import { UploadFileWidget } from '../widget';
import { ChipInputWidget } from '../widget/chipInputWidget/ChipInputWidget';
import { IVariationItem } from './ProductVariationAssets.interface';
import {
  IconWrapper,
  optionsStyle,
  StyledStack,
  StyledTableCell,
  StyledTypography,
  TableDivider,
} from './ProductVariationAssets.styled';

export const VariationAssetList = ({
  asset,
  themes,
  index,
  onChangeVariationTheme,
  deleteVariationAsset,
  assetUploadModal,
  onSelectIndex,
  groupProductAsset,
  onDYChange,
}: IVariationItem) => {
  const theme = useTheme();
  const deleteAssetModal = useModal();
  const { isViewOnly } = useUserRole();
  const isGroupAsset =
    groupProductAsset !== undefined &&
    asset?.assetName === groupProductAsset.name;
  const selectedTheme = useMemo(
    () =>
      themes
        ?.filter((theme) => asset.themeIds?.includes(theme.id))
        .map((theme) => theme.name),
    [themes, asset.themeIds],
  );

  const themeOptions = useMemo(
    () => themes?.map((theme) => ({ value: theme.id, label: theme.name })),
    [themes],
  );

  const handleThemeChange = useCallback(
    (value: unknown) => {
      if (
        Array.isArray(value) &&
        value.every((item) => typeof item === 'string')
      ) {
        onChangeVariationTheme(index, value);
      }
    },
    [index, onChangeVariationTheme],
  );

  const handleDeleteAsset = () => {
    deleteVariationAsset(index);
    deleteAssetModal.onCloseModal();
  };

  return (
    <>
      <TableDivider>
        <StyledStack xs={1}>
          <Radio
            checked={!!asset?.isDY}
            onChange={() => onDYChange(index)}
            disabled={isViewOnly}
            size="small"
          />
        </StyledStack>
        <StyledStack xs={3}>
          <UploadFileWidget
            name=""
            value={asset?.assetUrl}
            onChange={(value) => {
              if (value instanceof File) {
                onSelectIndex?.(index);
              }
            }}
            assetUploadModal={assetUploadModal}
            index={index}
            onSelectIndex={onSelectIndex}
            hasError={false}
            error=""
            options={{
              ...optionsStyle,
              imageBorderColor: theme.palette.divider,
            }}
            disabled={isViewOnly || isGroupAsset}
          />
          <Grid item xs={12}>
            <CustomTooltip
              placement="bottom"
              width={500}
              title={asset?.assetName}
              overflowLineNumber={1}>
              <StyledTypography>{asset?.assetName}</StyledTypography>
            </CustomTooltip>
          </Grid>
        </StyledStack>
        <Grid item xs={8}>
          {!isGroupAsset && (
            <StyledTableCell>
              <ChipInputWidget
                onChange={handleThemeChange}
                value={selectedTheme || []}
                selectOptions={themeOptions}
                disabled={isViewOnly}
              />
            </StyledTableCell>
          )}
          {!isGroupAsset && (
            <StyledTableCell>
              {asset?.inUse ? (
                <Tooltip title={lang.deleteAssetTooltip} arrow>
                  <IconWrapper size="small">
                    <IconButton
                      color={'default'}
                      onClick={() => deleteVariationAsset(index)}
                      disabled={asset?.inUse}>
                      {!isViewOnly && <DeleteOutlineIcon />}
                    </IconButton>
                  </IconWrapper>
                </Tooltip>
              ) : (
                <IconWrapper size="small">
                  <IconButton
                    color={'error'}
                    onClick={() => deleteAssetModal.onOpenModal()}
                    disabled={asset?.inUse}>
                    {!isViewOnly && <DeleteOutlineIcon />}
                  </IconButton>
                </IconWrapper>
              )}
            </StyledTableCell>
          )}
        </Grid>
      </TableDivider>
      <SimpleDialog
        actionColor="error"
        open={deleteAssetModal.open}
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
        title={lang.deleteAssetModal.title}
        onClose={deleteAssetModal.onCloseModal}
        onClickCancel={deleteAssetModal.onCloseModal}
        subTitle={lang.deleteAssetModal.subTitle}
        modalSize="sm"
        onClickConfirm={handleDeleteAsset}
      />
    </>
  );
};
